import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';

import { msalConfig } from 'b2c/b2cConf';
import {
	getAdminBillingStatuses,
	getBillingTypologies,
	getClaimsBillingStatuses,
	getPlanCodes,
} from 'feature/billing/billingSlice';
import {
	getReasonCodeCanceled,
	getReasonCodePending,
	getReasonCodeReject,
	getReasonCodeVoid,
} from 'feature/claims/claimSlice';
import {
	getComplaintOutcomes,
	getComplaintReasons,
} from 'feature/complaintTracking/complaintTrackingSlice';
import {
	getCarriers,
	getClaimsStatuses,
	getClients,
	getComplaintSources,
	getComplaintStatuses,
	getComplaintTypes,
	getFraudStatuses,
	getGroups,
	getNetworks,
	getRelationshipTypes,
	getReturnStatuses,
} from 'feature/form/formSlice';
import { logout } from 'feature/user/userSlice';
import { healthCheck } from 'feature/utils/checkConnectionSlice';
import { cleanupStorage } from 'utils/localStorage';

const findRightAccount = (accounts: AccountInfo[]) =>
	accounts.find(el => el.idTokenClaims?.aud === msalConfig.auth.clientId);

export const useLogin = () => {
	const { instance, accounts, inProgress } = useMsal();
	const account = useAccount();

	if (!account) {
		const rightAccount = findRightAccount(accounts);
		rightAccount && instance.setActiveAccount(rightAccount);
	}

	useEffect(() => {
		if (inProgress === InteractionStatus.None && !findRightAccount(accounts)) {
			const tokenRequest = {
				scopes: [],
			};
			cleanupStorage();
			instance.acquireTokenRedirect(tokenRequest);
		}
	}, [accounts, inProgress, instance]);
};

export const useLogout = () => {
	const { instance, accounts } = useMsal();
	const dispatch = useDispatch<AppDispatch>();

	return useCallback(() => {
		dispatch(logout({ instance, accounts }));
	}, [accounts, dispatch, instance]);
};

export const useCheckAlive = () => {
	const isAuthenticated = useIsAuthenticated();
	const dispatch = useDispatch<AppDispatch>();
	const { alive, tokenExpired } = useSelector(
		(state: RootState) => state.healthCheck,
	);

	const handleLogout = useLogout();

	useEffect(() => {
		if (isAuthenticated && alive === undefined) {
			dispatch(healthCheck(''));
		}
	}, [alive, dispatch, isAuthenticated]);

	useEffect(() => {
		if (tokenExpired) {
			handleLogout();
		}
	}, [dispatch, handleLogout, tokenExpired]);
};

export const useGetLocales = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { alive } = useSelector((state: RootState) => state.healthCheck);

	useEffect(() => {
		if (alive) {
			dispatch(getClaimsStatuses(''));
			dispatch(getNetworks(''));
		}
	}, [dispatch, alive]);

	useEffect(() => {
		if (alive) {
			dispatch(getClients(''));
		}
	}, [dispatch, alive]);

	useEffect(() => {
		if (alive) {
			dispatch(getReturnStatuses({}));
		}
	}, [dispatch, alive]);

	useEffect(() => {
		if (alive) dispatch(getReasonCodeVoid({}));
	}, [alive, dispatch]);

	useEffect(() => {
		if (alive) dispatch(getReasonCodeCanceled({}));
	}, [alive, dispatch]);

	useEffect(() => {
		if (alive) dispatch(getReasonCodePending({}));
	}, [alive, dispatch]);

	useEffect(() => {
		if (alive) dispatch(getReasonCodeReject({}));
	}, [alive, dispatch]);

	useEffect(() => {
		if (alive) dispatch(getCarriers(''));
	}, [alive, dispatch]);

	useEffect(() => {
		if (alive) dispatch(getGroups(''));
	}, [alive, dispatch]);

	useEffect(() => {
		if (alive) dispatch(getFraudStatuses(''));
	}, [alive, dispatch]);

	useEffect(() => {
		if (alive) {
			dispatch(getComplaintStatuses(''));
			dispatch(getComplaintSources(''));
			dispatch(getComplaintTypes(''));
			dispatch(getComplaintReasons(''));
			dispatch(getComplaintOutcomes(''));
			dispatch(getRelationshipTypes(''));

			dispatch(getClaimsBillingStatuses(''));
			dispatch(getAdminBillingStatuses(''));
			dispatch(getBillingTypologies(''));
			dispatch(getPlanCodes(''));
		}
	}, [alive, dispatch]);
};
